<template>
  <div :class="$style.section">
    <h3 :class="$style.sectionTitle">
      <ChartIcon :size="20" />
      Daily Duration
    </h3>

    <div v-if="dailyDuration.dailyData.length === 0" :class="$style.noData">
      No completed tasks with duration in the selected period.
    </div>

    <div v-else :class="$style.chartContainer">
      <div :class="$style.distributionList">
        <div
          v-for="item in dailyDuration.dailyData"
          :key="item.date"
          :class="$style.distributionItem"
        >
          <span :class="$style.distributionDate">{{
            formatDate(item.date)
          }}</span>
          <div :class="$style.distributionBar">
            <div
              :class="$style.distributionBarFill"
              :style="{ width: `${item.percentage}%` }"
            ></div>
          </div>
          <span :class="$style.distributionValue">
            {{ formatDuration(item.totalDuration) }}
            <span :class="$style.sessionCount"
              >({{ item.sessionCount }} sessions)</span
            >
          </span>
        </div>
      </div>

      <!-- Totals Section -->
      <div :class="$style.totalsSection">
        <div :class="$style.totalItem">
          <span :class="$style.totalLabel">Total Duration:</span>
          <span :class="$style.totalValue">{{ getTotalDuration() }}</span>
        </div>
        <div :class="$style.totalItem">
          <span :class="$style.totalLabel">Average per Day:</span>
          <span :class="$style.totalValue">{{ getAverageDuration() }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDuration } from "./utils";
import { ChartIcon } from "../icons";
import type { DailyDuration } from "../../services/analyticsService";

interface Props {
  dailyDuration: DailyDuration;
}

const props = defineProps<Props>();

const getTotalDuration = (): string => {
  const total = props.dailyDuration.dailyData.reduce(
    (sum, item) => sum + item.totalDuration,
    0
  );
  return formatDuration(total);
};

const getAverageDuration = (): string => {
  const data = props.dailyDuration.dailyData;
  if (data.length === 0) return formatDuration(0);

  const total = data.reduce((sum, item) => sum + item.totalDuration, 0);
  const average = total / data.length;
  return formatDuration(average);
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  // Compare dates without time component
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const yesterdayOnly = new Date(
    yesterday.getFullYear(),
    yesterday.getMonth(),
    yesterday.getDate()
  );

  if (dateOnly.getTime() === todayOnly.getTime()) {
    return "Today";
  } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
    return "Yesterday";
  }

  // For other dates, use a more friendly format
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
  };

  // Add year if not current year
  if (date.getFullYear() !== today.getFullYear()) {
    options.year = "numeric";
  }

  return date.toLocaleDateString("en-US", options);
};
</script>

<style module>
.section {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.sectionTitle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem 0;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
}

.noData {
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  padding: 2rem;
  font-style: italic;
}

.chartContainer {
  margin-top: 1rem;
}

.distributionList {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.distributionItem {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 0;
}

.distributionDate {
  min-width: 100px;
  font-weight: 500;
  color: white;
  font-size: 0.9rem;
}

.distributionBar {
  flex: 1;
  height: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  overflow: hidden;
}

.distributionBarFill {
  height: 100%;
  background: linear-gradient(90deg, #00b894, #74b9ff);
  border-radius: 5px;
  transition: width 0.3s ease;
}

.distributionValue {
  min-width: 120px;
  text-align: right;
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
}

.sessionCount {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
  font-size: 0.8rem;
  margin-left: 4px;
}

.totalsSection {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.totalItem {
  display: flex;
  align-items: center;
  gap: 8px;
}

.totalLabel {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  font-weight: 500;
}

.totalValue {
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .totalsSection {
    flex-direction: column;
    gap: 0.5rem;
  }

  .totalItem {
    justify-content: space-between;
  }
}
</style>
